import { useSyncExternalStore } from "react";
import type {
  MeetingClientPickerSource,
  MeetingRoleAccess,
  MeetingWorkspaceConfigResponse,
  TranscriptSpeakerRoleSlot,
} from "@/services/api/meetings/types";
import type { MeetingStatusBadgeColor } from "@/components/meetings/meetingStatusUtils";
import type { MeetingTypeDefinition, MeetingTabId } from "@/components/meetings/meetingTypeRegistry";
import type {
  MeetingDocumentTemplate,
  MeetingWorkspaceProfile,
} from "@/components/meetings/meetingWorkspaceProfiles";

let activeConfig: MeetingWorkspaceConfigResponse | null = null;
const listeners = new Set<() => void>();

function notifyListeners(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setMeetingWorkspaceConfig(config: MeetingWorkspaceConfigResponse | null): void {
  activeConfig = config;
  notifyListeners();
}

export function getMeetingWorkspaceConfig(): MeetingWorkspaceConfigResponse | null {
  return activeConfig;
}

export function useMeetingWorkspaceConfig(): MeetingWorkspaceConfigResponse | null {
  return useSyncExternalStore(subscribe, getMeetingWorkspaceConfig, () => null);
}

function apiTypeToDefinition(type: MeetingWorkspaceConfigResponse["meetingTypes"][number]): MeetingTypeDefinition {
  return {
    key: type.key,
    label: type.label,
    tabs: type.tabs as MeetingTabId[],
    badgeColor: type.badgeColor as MeetingStatusBadgeColor,
    detailView: type.detailView,
  };
}

export function getConfiguredMeetingTypeDefinition(typeKey: string | null | undefined): MeetingTypeDefinition | null {
  if (!activeConfig) return null;
  const key = (typeKey ?? "").trim().toLowerCase();
  const match = activeConfig.meetingTypes.find((t) => t.key === key && t.enabled !== false);
  if (match) return apiTypeToDefinition(match);
  return null;
}

export function getConfiguredMeetingAccess(): MeetingRoleAccess | null {
  return activeConfig?.meetingAccess ?? null;
}

export function listConfiguredMeetingTypes(): MeetingTypeDefinition[] {
  if (!activeConfig) return [];
  const allowed = new Set(getConfiguredMeetingAccess()?.allowedMeetingTypeKeys ?? []);
  const types = activeConfig.meetingTypes.filter((t) => t.enabled !== false);
  const filtered = allowed.size ? types.filter((t) => allowed.has(t.key)) : types;
  return filtered.map(apiTypeToDefinition);
}

export function getConfiguredDefaultMeetingType(): string | null {
  return getConfiguredMeetingAccess()?.defaultMeetingType ?? activeConfig?.defaultMeetingType ?? null;
}

export function getConfiguredWorkspaceProfile(): MeetingWorkspaceProfile | null {
  if (!activeConfig?.workspaceProfile) return null;
  return activeConfig.workspaceProfile;
}

export function getConfiguredSyncTarget(): "crm" | "fund_db" | null {
  return activeConfig?.syncTarget ?? null;
}

export function getConfiguredClientPickerSource(): MeetingClientPickerSource {
  return activeConfig?.clientPickerSource ?? "fa_clients";
}

export function getConfiguredLinkedClientHelpText(): string {
  const fromApi = activeConfig?.clientPickerHelpText?.trim();
  if (fromApi) return fromApi;
  if (getConfiguredClientPickerSource() === "progeny_hub") {
    return "Link this meeting to a client account in Progeny CRM.";
  }
  if (getConfiguredSyncTarget() === "crm") {
    return "Link this meeting to a client account in Curo.";
  }
  return "Link this meeting to a client record.";
}

export function getTranscriptionSpeakerRoles(
  meetingType: string | null | undefined,
): TranscriptSpeakerRoleSlot[] {
  const typeCfg = typeConfigForKey(meetingType);
  return typeCfg?.transcriptionSpeakerRoles ?? [];
}

export function getConfiguredMissionKitId(typeKey: string | null | undefined): string | null {
  const key = (typeKey ?? "").trim().toLowerCase();
  const match = activeConfig?.meetingTypes.find((t) => t.key === key);
  return match?.missionKitId ?? null;
}

/** Fallback when API type config omits `expectedArtefactIds` (by mission kit, not generic annual_review). */
const EXPECTED_ARTEFACT_IDS_BY_MISSION_KIT: Record<string, string[]> = {
  progeny_annual_review: [
    "aml_pre_meeting_healthcheck",
    "portfolio_assessment",
    "pre_meeting_brief",
    "post_summary",
    "file_note",
    "meeting_sentiment_analysis",
    "handover_notes",
    "aml_pre_letter_review",
    "suitability_letter",
  ],
  saltus_annual_review: [
    "pre_meeting_brief",
    "post_summary",
    "file_note",
    "risk_profiling_pack",
    "handover_notes",
    "suitability_letter",
  ],
};

/** Ordered expected artefact slots for a meeting type (falls back to workspace profile order). */
export function getExpectedArtefactIds(
  meetingType: string | null | undefined,
  profileArtefactIds: string[],
): string[] {
  const typeCfg = typeConfigForKey(meetingType);
  const fromType = typeCfg?.expectedArtefactIds?.filter(Boolean) ?? [];
  if (fromType.length) return fromType;
  const kitId = typeCfg?.missionKitId;
  if (kitId && EXPECTED_ARTEFACT_IDS_BY_MISSION_KIT[kitId]?.length) {
    return EXPECTED_ARTEFACT_IDS_BY_MISSION_KIT[kitId];
  }
  if (profileArtefactIds.length) return profileArtefactIds;
  return [];
}

function typeConfigForKey(typeKey: string | null | undefined): MeetingWorkspaceConfigResponse["meetingTypes"][number] | null {
  const key = (typeKey ?? "").trim().toLowerCase();
  return activeConfig?.meetingTypes.find((t) => t.key === key) ?? null;
}

export function getBriefingTemplateForMeetingType(
  meetingType: string | null | undefined,
  profile: MeetingWorkspaceProfile,
): MeetingDocumentTemplate {
  const typeTpl = typeConfigForKey(meetingType)?.briefingTemplate;
  if (typeTpl) return typeTpl;
  return profile.briefingTemplate;
}

export function getSummaryTemplateForMeetingType(
  meetingType: string | null | undefined,
  profile: MeetingWorkspaceProfile,
): MeetingDocumentTemplate {
  const typeTpl = typeConfigForKey(meetingType)?.summaryTemplate;
  if (typeTpl) return typeTpl;
  return profile.summaryTemplate;
}

export function isMeetingTypeAllowedForCurrentUser(typeKey: string): boolean {
  const access = getConfiguredMeetingAccess();
  if (!access) return true;
  const enabledCount =
    activeConfig?.meetingTypes.filter((t) => t.enabled !== false).length ?? 0;
  if (enabledCount <= 1) return true;
  return access.allowedMeetingTypeKeys.includes(typeKey.trim().toLowerCase());
}
